import * as React from 'react';
import Liability from "../../../model/Liability";
import {FormLabel, Grid, Typography, useTheme} from "@mui/material";
import ModelCard2 from "../../../components/model/ModelCard2";
import {useEffect, useState} from "react";
import {
  dateToLocalFormat,
  numberToMoneyFormat, numberToPercentFormat,
} from "../../../stores/StoreUtilities";
import Model from "../../../model/Model";
import Snapshot, {ISnapshotDetail} from "../../../model/Snapshot";
import {LiabilityIcon} from "../../../styles/Icons";
import LiabilityEditDialog from "./LiabilityEditDialog";
import {isNumber} from "../../../model/ModelUtilities";
import {useResources} from "../../../stores/ResourceProvider";
import {compareAsc, startOfToday} from "date-fns";

const LiabilityCard = ({
  liability,
  model,
  snapshot,
  expanded,
  onChange,
  onSave,
  onDelete,
  onArchive,
  dragHandleProps
}: {
  liability: Liability
  model: Model
  snapshot?: Snapshot
  expanded?: boolean
  onChange?(event: any, expanded: boolean): void
  onSave?(expense: Liability, snapshotItem?: ISnapshotDetail): void
  onDelete?(deleted: Liability): void
  onArchive?(asset: Liability): void
  dragHandleProps?: any
}) => {
  const [liabilityModel, setLiabilityModel] = useState<Liability>(liability)
  const [snapshotDetail, setSnapshotDetail] = useState<ISnapshotDetail | undefined>()
  const [description, setDescription] = useState<string>("")
  const [owner, setOwner] = useState<string | undefined>()
  const [balance, setBalance] = useState<string | undefined>()
  const [balanceDate, setBalanceDate] = useState<string | undefined>()
  const [value, setValue] = useState<string | undefined>()
  const [payoffDate, setPayoffDate] = useState<string | undefined>()
  const [interestRate, setInterestRate] = useState<string | undefined>()
  const [archived, setArchived] = useState<boolean>(false)
  const [editOpen, setEditOpen] = useState<boolean>(false)
  const [saved, setSaved] = useState<boolean>(false)

  const {modelStore} = useResources()

  useEffect(() => {
    init(liability)
  }, [liability, snapshot])

  const init = (liability: Liability) => {
    setLiabilityModel(liability)
    setDescription(liability.description)
    setOwner(model.getPerson(liability.ownerId)?.nickname ?? Model.jointNickname)
    const itemBalance = modelStore.getNetWorthItemBalanceDetail(liability, model, snapshot)
    setSnapshotDetail(itemBalance.detail)
    setBalance(numberToMoneyFormat(itemBalance.balance, 0))
    setBalanceDate((dateToLocalFormat(itemBalance.balanceDate)))
    const startDate = liability.startDate
    if (startDate && compareAsc(startOfToday(), startDate) < 0) {
      setValue("$0")
    } else {
      setValue(numberToMoneyFormat(itemBalance.balance, 0))
    }
    setPayoffDate(dateToLocalFormat(liability.endDate))
    setInterestRate(isNumber(liability.interestRate) ? numberToPercentFormat(liability.interestRate, 2) : "")
    setArchived(liability.archived)
    setEditOpen(!liability.createdAt)
  }

  const theme = useTheme()

  const styles = {
    heading: {
      color: theme.palette.primary.main,
      fontWeight: 'bold'
    },
    row: {
      height: 40,
      minHeight: 40,
      maxHeight: 40,
    },
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: "1.0em",
      maxHeight: 15,
      marginBottom: "1px",
      textWrap: "nowrap",
      color: theme.palette.grey["700"]
    },
    liabilityIcon: {
      color: theme.palette.primary.contrastText,
      backgroundColor: "#881E10",
      height: 20,
      padding: "4px",
      borderRadius: "5px"
    }
  }

  const renderContent = () => {
    return (
      <Grid container direction="column" spacing={0}>
        <Grid item xs={12} sx={styles.row}>
          <Grid container direction="row" spacing={0}>
            <Grid item xs={4}>
              <FormLabel sx={styles.formLabel}>Balance</FormLabel>
              <Typography variant="body2">
                {balance}
              </Typography>
            </Grid>
              <Grid item xs={8}>
                <FormLabel sx={styles.formLabel}>Balance Date</FormLabel>
                <Typography variant="body2">
                  {balanceDate}
                </Typography>
              </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={styles.row}>
          <Grid container direction="row" spacing={0}>
            <Grid item xs={4}>
              <FormLabel sx={styles.formLabel}>Owner</FormLabel>
              <Typography variant="body2">
                {owner}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <FormLabel sx={styles.formLabel}>Payoff Date</FormLabel>
              <Typography variant="body2" color={liability.end ? "inherit" : "darkgrey"}>
                {payoffDate}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <FormLabel sx={styles.formLabel}>Interest Rate %</FormLabel>
              <Typography variant="body2">
                {interestRate}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  return (
    <React.Fragment>
      <ModelCard2
        icon={<LiabilityIcon/>}
        title={description}
        value={value}
        valueColor="#881E10"
        archived={archived}
        expanded={expanded}
        onChange={onChange}
        editButtonId={`edit-btn-${liabilityModel.id}`}
        onEdit={(event: any) => {
          setEditOpen(!editOpen)
        }}
        dragHandleProps={dragHandleProps}
      >
        {renderContent()}
      </ModelCard2>
      {editOpen &&
        <LiabilityEditDialog liability={liabilityModel} model={model} snapshot={snapshot}
                             open={editOpen}
                             onSave={(update: Liability, snapshotItem?: ISnapshotDetail) => {
                               init(update)
                               setEditOpen(false)
                               if (onSave) {
                                 onSave(update, snapshotItem)
                                 setSaved(true)
                               }
                             }}
                             onDelete={(deleted: Liability) => {
                               setEditOpen(false)
                               if (onDelete) {
                                 onDelete(deleted)
                               }
                             }}
                             onArchive={(liability: Liability) => {
                               setEditOpen(false)
                               if (onArchive) {
                                 onArchive(liability)
                               }
                             }}
                             onClose={() => {
                               setEditOpen(false)
                               if (!liabilityModel.createdAt && onDelete) {
                                 onDelete(liabilityModel)
                               }
                             }}/>
      }
    </React.Fragment>
  )
}

export default LiabilityCard